import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Loader2, Check, Sparkles, Users, Trash2, RefreshCw, AlertTriangle, X, GitMerge, Mail, ShieldCheck, IdCard, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { DangerZone } from './ClientProfileShared';
import { EmailPreviewModal } from './AdminSiteSettingsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Chaque formule inclut automatiquement les fonctionnalités des formules en dessous
// (ex: Coaching Hybride inclut aussi Communauté + Calculateur payant). Cela reflète
// exactement la logique d'accès déjà utilisée partout dans l'app (un seul champ `plan`).
const TIERS = [
  { plan: null, label: 'Aucun accès', desc: 'Aucune fonctionnalité payante', features: [] },
  { plan: 'monthly', label: 'Calculateur payant', desc: 'Fonctionnalités avancées du calculateur uniquement', features: ['Calculateur — fonctionnalités payantes'] },
  { plan: 'solo', label: 'Suivi Pro+', desc: 'Suivi Pro + module musculation complet (DIY, sans coach humain)', features: ['Calculateur — fonctionnalités payantes', 'Module musculation (bibliothèque + programmes DIY + IA à l\'usage)'] },
  { plan: 'communaute', label: 'Communauté Body-Up', desc: 'Accès à la communauté, sans coaching personnalisé', features: ['Communauté Body-Up'] },
  { plan: 'transformation', label: 'Coaching 100% en ligne', desc: 'Coaching en ligne + tout ce qui précède', features: ['Calculateur — fonctionnalités payantes', 'Communauté Body-Up', 'Coaching 100% en ligne (visios Zoom)'] },
  { plan: 'vip', label: 'Coaching Hybride', desc: 'Coaching hybride (studio + visio) + tout ce qui précède', features: ['Calculateur — fonctionnalités payantes', 'Communauté Body-Up', 'Coaching Hybride (studio + Zoom + Scan 3D)'] },
];

const PLAN_BADGE = {
  vip: { label: 'Coaching Hybride', className: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  transformation: { label: 'Coaching en ligne', className: 'bg-primary/15 text-primary border-primary/30' },
  communaute: { label: 'Communauté', className: 'bg-pink-500/15 text-pink-300 border-pink-500/30' },
  solo: { label: 'Suivi Pro+', className: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  monthly: { label: 'Calculateur payant', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  yearly: { label: 'Calculateur payant (annuel)', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
};

const PlanBadge = ({ plan, status }) => {
  const isActive = status === 'active';
  if (!plan || !isActive) {
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-zinc-700/40 text-zinc-500 border-zinc-600/30">Aucun accès</span>;
  }
  const b = PLAN_BADGE[plan] || { label: plan, className: 'bg-zinc-700/40 text-zinc-300 border-zinc-600/30' };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${b.className}`}>{b.label}</span>;
};

export const ClientAccessPanel = ({ csrfToken, onLogout, fmtDate, onGoToClients }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [plan, setPlan] = useState(null);
  const [periodEnd, setPeriodEnd] = useState('');
  const [creating, setCreating] = useState(false);

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // { email, user_id }

  const [duplicates, setDuplicates] = useState({ count: 0, groups: [] });
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [merging, setMerging] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setClients(data.clients || []);
    } catch {
      // silencieux — la liste n'est pas critique au chargement
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const fetchDuplicates = useCallback(async () => {
    setCheckingDuplicates(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/duplicates`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setDuplicates(data);
    } catch {
      // silencieux
    } finally {
      setCheckingDuplicates(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchDuplicates();
  }, [fetchClients, fetchDuplicates]);

  const createClient = async () => {
    if (!email.trim()) { toast.error('Email requis'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/create`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ email: email.trim().toLowerCase(), name: name.trim(), plan, current_period_end: periodEnd || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(`Client ${email} créé — un email lui a été envoyé pour définir son mot de passe.`);
      setEmail(''); setName(''); setPlan(null); setPeriodEnd('');
      fetchClients();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const mergeGroup = async (primaryEmail, group) => {
    const others = group.filter((g) => g.email !== primaryEmail).map((g) => g.email);
    if (!others.length) return;
    if (!window.confirm(`Fusionner ${others.join(', ')} dans ${primaryEmail} ? Cette action est irréversible.`)) return;
    setMerging(primaryEmail);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/merge`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ primary_email: primaryEmail, duplicate_emails: others, confirm: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(`Comptes fusionnés dans ${primaryEmail}`);
      fetchClients();
      fetchDuplicates();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMerging(null);
    }
  };

  const previewWelcomeEmail = async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/welcome-email-preview?plan=${plan || ''}&language=fr`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setEmailPreview(data);
    } catch {
      toast.error("Impossible de charger l'aperçu.");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="client-access-panel">
      {duplicates.count > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="duplicates-banner">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-amber-200 text-sm">
              <span className="font-bold">{duplicates.count} doublon{duplicates.count > 1 ? 's' : ''} potentiel{duplicates.count > 1 ? 's' : ''} détecté{duplicates.count > 1 ? 's' : ''}</span> — même nom ou téléphone avec des emails différents.
            </p>
          </div>
          <button onClick={() => setShowDuplicates(true)} className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-amber-300 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 inline-flex items-center gap-1.5" data-testid="view-duplicates-btn">
            <GitMerge className="w-3.5 h-3.5" /> Voir les doublons
          </button>
        </div>
      )}

      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5">
        <h3 className="flex items-center gap-2 font-bold text-white mb-4">
          <UserPlus className="w-5 h-5 text-primary" /> Créer un nouveau client
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="email@client.ch"
            className="px-3 py-2.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
            data-testid="new-client-email"
          />
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Nom (optionnel)"
            className="px-3 py-2.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
            data-testid="new-client-name"
          />
        </div>

        <p className="text-zinc-500 text-xs mb-2">Fonctionnalités à activer :</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
          {TIERS.map((t) => (
            <button
              key={t.label}
              onClick={() => setPlan(t.plan)}
              className={`text-left p-3 rounded-xl border-2 transition-all ${plan === t.plan ? 'border-primary bg-primary/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
              data-testid={`tier-option-${t.plan || 'none'}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-white font-semibold text-sm">{t.label}</span>
                {plan === t.plan && <Check className="w-4 h-4 text-primary" />}
              </div>
              <p className="text-zinc-500 text-[11px] mb-2">{t.desc}</p>
              {t.features.map((f) => (                <div key={f} className="flex items-center gap-1.5 text-[10px] text-emerald-300/90">
                  <Check className="w-3 h-3 shrink-0" /> {f}
                </div>
              ))}
            </button>
          ))}
        </div>

        {plan && (
          <div className="mb-4 max-w-xs">
            <label className="text-zinc-500 text-xs mb-1.5 block">Date de fin d'accès (optionnel — vide = accès permanent)</label>
            <input
              type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
              data-testid="new-client-period-end"
            />
          </div>
        )}

        <button
          onClick={createClient}
          disabled={creating || !email.trim()}
          className="px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          data-testid="create-client-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Créer le client
        </button>
        <button
          onClick={previewWelcomeEmail}
          disabled={previewLoading}
          className="ml-2 px-4 py-2.5 rounded-xl font-bold text-zinc-300 bg-white/5 border border-white/10 hover:border-white/25 disabled:opacity-40 inline-flex items-center gap-2"
          data-testid="preview-welcome-email-btn"
        >
          {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          Aperçu de l'email
        </button>
      </div>

      {emailPreview && <EmailPreviewModal html={emailPreview.html} subject={emailPreview.subject} onClose={() => setEmailPreview(null)} />}

      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="flex items-center gap-2 font-bold text-white">
            <Users className="w-5 h-5 text-primary" /> Tous les clients ({clients.length})
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={fetchDuplicates} disabled={checkingDuplicates} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 bg-white/5 hover:bg-white/10 border border-white/10 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="find-duplicates-btn">
              {checkingDuplicates ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5" />} Rechercher les doublons
            </button>
            <button onClick={fetchClients} disabled={loadingClients} className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-50" data-testid="refresh-clients-btn" title="Rafraîchir">
              <RefreshCw className={`w-4 h-4 ${loadingClients ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {loadingClients ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
        ) : clients.length === 0 ? (
          <p className="text-zinc-500 text-sm py-4 text-center">Aucun client pour le moment.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm min-w-[640px]" data-testid="clients-table">
              <thead>
                <tr className="text-zinc-500 text-xs uppercase tracking-wide border-b border-white/10">
                  <th className="text-left px-2 py-2 font-semibold">Client</th>
                  <th className="text-left px-2 py-2 font-semibold">Accès</th>
                  <th className="text-left px-2 py-2 font-semibold">Créé le</th>
                  <th className="text-right px-2 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.email} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`client-row-${c.email}`}>
                    <td className="px-2 py-2.5">
                      <div className="text-white font-semibold">{c.name || '—'}</div>
                      <div className="text-zinc-500 text-xs inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>
                    </td>
                    <td className="px-2 py-2.5"><PlanBadge plan={c.plan} status={c.plan_status} /></td>
                    <td className="px-2 py-2.5 text-zinc-500 text-xs">{fmtDate ? fmtDate(c.created_at) : (c.created_at || '').slice(0, 10)}</td>
                    <td className="px-2 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => onGoToClients && onGoToClients(c.email)}
                          className="p-1.5 rounded-lg text-zinc-400 hover:text-primary hover:bg-white/5"
                          data-testid={`view-client-profile-btn-${c.email}`}
                          title="Voir la fiche complète (onglet Clients)"
                        >
                          <IdCard className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget({ email: c.email, user_id: c.user_id })}
                          className="p-1.5 rounded-lg text-red-400/80 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-client-btn-${c.email}`}
                          title="Supprimer / anonymiser"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="delete-client-modal" onClick={() => setDeleteTarget(null)}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-white text-sm">{deleteTarget.email}</h4>
              <button onClick={() => setDeleteTarget(null)} data-testid="close-delete-modal-btn"><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
            </div>
            <DangerZone
              userId={deleteTarget.user_id}
              email={deleteTarget.email}
              csrfToken={csrfToken}
              onDeleted={() => { setDeleteTarget(null); fetchClients(); fetchDuplicates(); }}
            />
          </div>
        </div>
      )}

      {showDuplicates && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="duplicates-modal" onClick={() => setShowDuplicates(false)}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="flex items-center gap-2 font-bold text-white"><GitMerge className="w-4.5 h-4.5 text-amber-400" /> Doublons potentiels</h4>
              <button onClick={() => setShowDuplicates(false)} data-testid="close-duplicates-modal-btn"><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
            </div>
            {duplicates.groups.length === 0 ? (
              <p className="text-zinc-500 text-sm">Aucun doublon détecté pour le moment.</p>
            ) : (
              <div className="space-y-4">
                {duplicates.groups.map((group, gi) => (
                  <div key={gi} className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid={`duplicate-group-${gi}`}>
                    <div className="space-y-2 mb-3">
                      {group.map((g) => (
                        <div key={g.email} className="flex items-center justify-between text-sm">
                          <div>
                            <span className="text-white font-semibold">{g.name || '—'}</span>{' '}
                            <span className="text-zinc-500">· {g.email}</span>
                            {g.phone && <span className="text-zinc-500"> · {g.phone}</span>}
                          </div>
                          <button
                            onClick={() => mergeGroup(g.email, group)}
                            disabled={merging === g.email}
                            className="px-3 py-1 rounded-lg text-xs font-bold text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 inline-flex items-center gap-1.5 disabled:opacity-50"
                            data-testid={`merge-into-${g.email}`}
                          >
                            {merging === g.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                            Fusionner ici
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-zinc-500 text-[11px]">Cliquez "Fusionner ici" sur le compte à conserver — les autres seront fusionnés dedans (données, paiements, abonnement) puis supprimés.</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <button
        onClick={() => onGoToClients && onGoToClients()}
        className="w-full flex items-center justify-between gap-3 rounded-2xl border border-primary/25 bg-primary/5 hover:bg-primary/10 p-4 transition-colors text-left"
        data-testid="goto-clients-tab-btn"
      >
        <span className="flex items-center gap-2 text-white font-semibold text-sm">
          <IdCard className="w-4.5 h-4.5 text-primary" /> Gérer un client existant (profil complet, VIP, factures, plan…)
        </span>
        <span className="text-primary text-xs font-bold">Onglet Clients →</span>
      </button>
    </div>
  );
};

export default ClientAccessPanel;
